/**
 * Deterministic Pixelated Avatar Generator
 * 
 * Creates a unique, blocky/pixelated profile picture based on a seed value
 * (like wallet address, user ID, or handle).
 */

// Color palette for avatars - vibrant and distinguishable
const AVATAR_COLORS = [
  '#8b5cf6', // Purple
  '#06b6d4', // Cyan
  '#10b981', // Emerald
  '#f59e0b', // Amber
  '#ef4444', // Red
  '#ec4899', // Pink
  '#6366f1', // Indigo
  '#14b8a6', // Teal
  '#f97316', // Orange
  '#84cc16', // Lime
];

// Simple hash function for deterministic randomness
function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

// Seeded random number generator
function seededRandom(seed) {
  const x = Math.sin(seed++) * 10000;
  return x - Math.floor(x);
}

/**
 * Generate a pixelated avatar as a data URL
 * 
 * @param {string} seed - Unique identifier (wallet address, ID, handle, etc.)
 * @param {number} size - Canvas size in pixels (default 64)
 * @param {number} gridSize - Grid resolution (default 5x5)
 * @returns {string} Data URL of the generated avatar
 */
export function generateAvatar(seed, size = 64, gridSize = 5) {
  const hash = hashString(seed || 'default');
  
  // Pick colors based on seed
  const bgColorIndex = hash % AVATAR_COLORS.length;
  const fgColorIndex = (hash + 3) % AVATAR_COLORS.length;
  const bgColor = AVATAR_COLORS[bgColorIndex];
  const fgColor = AVATAR_COLORS[fgColorIndex === bgColorIndex ? (fgColorIndex + 1) % AVATAR_COLORS.length : fgColorIndex];
  
  // Create canvas
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  
  // Fill background
  ctx.fillStyle = bgColor;
  ctx.fillRect(0, 0, size, size);
  
  const cellSize = size / gridSize;
  const halfGrid = Math.ceil(gridSize / 2);
  
  // Generate symmetric pattern
  for (let y = 0; y < gridSize; y++) {
    for (let x = 0; x < halfGrid; x++) {
      const seedValue = hash + y * gridSize + x;
      const filled = seededRandom(seedValue) > 0.5;
      
      if (filled) {
        ctx.fillStyle = fgColor;
        // Draw pixel on left side
        ctx.fillRect(x * cellSize, y * cellSize, cellSize, cellSize);
        // Mirror on right side for symmetry
        ctx.fillRect((gridSize - 1 - x) * cellSize, y * cellSize, cellSize, cellSize);
      }
    }
  }
  
  return canvas.toDataURL('image/png');
}

/**
 * React component-friendly hook for avatar generation
 * Returns the avatar data URL and regenerates on seed change
 */
export function useAvatar(seed, size = 64, gridSize = 5) {
  // Generate on first render and when seed changes
  return generateAvatar(seed, size, gridSize);
}

export default generateAvatar;
